// SPDX-License-Identifier: GPL-3.0-or-later
//
// AI-driven change requests against a generated Typst source. The model is
// asked to return a list of literal find/replace edits rather than a full
// rewrite — that keeps user-made manual tweaks intact and lets the UI show
// each edit as a reviewable card.

import { complete } from './client';
import { parseJson } from './parse';

export interface TypstPatchEdit {
  find: string;
  replace: string;
  description?: string;
}

export interface TypstPatch {
  edits: TypstPatchEdit[];
  note?: string;
}

function isTypstPatch(x: unknown): x is TypstPatch {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (!Array.isArray(o['edits'])) return false;
  for (const e of o['edits']) {
    if (!e || typeof e !== 'object') return false;
    const r = e as Record<string, unknown>;
    if (typeof r['find'] !== 'string' || typeof r['replace'] !== 'string') return false;
    if (r['description'] !== undefined && typeof r['description'] !== 'string') return false;
  }
  if (o['note'] !== undefined && typeof o['note'] !== 'string') return false;
  return true;
}

export type DocKind = 'cv' | 'cover' | 'interview';

const KIND_LABEL: Record<DocKind, string> = {
  cv: 'tailored CV',
  cover: 'cover letter',
  interview: 'interview prep brief',
};

const PATCH_SCHEMA = `{
  "edits": [
    {
      "find": string,        // exact, contiguous substring from the CURRENT Typst source
      "replace": string,     // the new substring that should replace it
      "description"?: string // one short sentence (<=80 chars) describing the change
    }
  ],
  "note"?: string            // optional one-line summary of what changed overall
}`;

export const patchSystemPrompt = `You edit a Typst (.typ) document for the user.

Output ONLY valid JSON matching this shape — no markdown, no commentary:

${PATCH_SCHEMA}

Rules:
- Each "find" MUST be an exact substring of the current source. Copy it byte-for-byte, including whitespace and Typst syntax (# functions, brackets, quotes). Do not paraphrase.
- Each "find" MUST be unique in the source; include enough surrounding context to disambiguate if a short string appears multiple times.
- Prefer many small, targeted edits over one giant replace.
- Preserve the document's Typst structure (function calls like #cv-section[...], lists, escapes). Edit only the user-visible text inside those constructs unless the user explicitly asks to change layout.
- Never invent companies, roles, dates, technologies, or quantified results that aren't supported by the profile. If a claim isn't in the profile, leave it out.
- If the request is impossible or unsafe (e.g. asks to fabricate experience), return {"edits": [], "note": "explanation"}.
- Return {"edits": []} if no change is needed.`;

export interface PatchPromptInputs {
  kind: DocKind;
  source: string;
  profileText: string;
  jobTitle: string;
  companyName: string;
  jobUrl: string;
  jobDescription: string;
  userRequest: string;
}

export function patchUserPrompt(i: PatchPromptInputs): string {
  return `You are editing the user's ${KIND_LABEL[i.kind]} for this role.

<profile>
${i.profileText.trim() || '(profile is empty)'}
</profile>

<job>
Title: ${i.jobTitle}
Company: ${i.companyName}
URL: ${i.jobUrl}

${i.jobDescription.trim() || '(no description recorded)'}
</job>

<current-typst-source>
${i.source}
</current-typst-source>

<user-request>
${i.userRequest.trim()}
</user-request>

Produce the JSON patch. Output only the JSON.`;
}

export interface PatchSuccess {
  ok: true;
  patch: TypstPatch;
}

export interface PatchFailure {
  ok: false;
  stage: 'ai' | 'parse';
  message: string;
  raw?: string;
}

export async function requestPatch(inputs: PatchPromptInputs): Promise<PatchSuccess | PatchFailure> {
  let aiContent: string;
  try {
    const res = await complete({
      system: patchSystemPrompt,
      user: patchUserPrompt(inputs),
      maxTokens: 3000,
    });
    aiContent = res.content;
  } catch (err) {
    return { ok: false, stage: 'ai', message: err instanceof Error ? err.message : String(err) };
  }
  const parsed = parseJson(aiContent, isTypstPatch);
  if (!parsed.ok) return { ok: false, stage: 'parse', message: parsed.message, raw: parsed.raw };
  return { ok: true, patch: parsed.value };
}

export interface AppliedEdit extends TypstPatchEdit {
  applied: boolean;
  reason?: string;
}

export interface ApplyResult {
  output: string;
  edits: AppliedEdit[];
}

// Apply edits left-to-right, refusing duplicates so we don't accidentally edit
// two occurrences when the model only meant one. Each edit must match exactly
// once in the *current* output.
export function applyPatch(source: string, patch: TypstPatch): ApplyResult {
  let out = source;
  const edits: AppliedEdit[] = [];
  for (const edit of patch.edits) {
    if (edit.find === edit.replace) {
      edits.push({ ...edit, applied: false, reason: 'find and replace are identical' });
      continue;
    }
    if (edit.find.length === 0) {
      edits.push({ ...edit, applied: false, reason: 'find is empty' });
      continue;
    }
    const first = out.indexOf(edit.find);
    if (first === -1) {
      edits.push({ ...edit, applied: false, reason: 'find string not found in source' });
      continue;
    }
    const second = out.indexOf(edit.find, first + 1);
    if (second !== -1) {
      edits.push({ ...edit, applied: false, reason: 'find string is not unique in source' });
      continue;
    }
    out = out.slice(0, first) + edit.replace + out.slice(first + edit.find.length);
    edits.push({ ...edit, applied: true });
  }
  return { output: out, edits };
}
